/******************************************************************************\
 * :$
 *
 * Copyright(c) 2022 SAS Institute Inc., Cary, NC, USA. All Rights Reserved.
 *
 * Name: ResetPassword
 *
 * Purpose:
 *
 * Author: craig (Craig.Simpson@sas.com)
 *
 * Support: SAS(r) Solutions OnDemand
 *
 * Input:
 *
 * Output:
 *
 * Parameters: (if applicable)
 *
 * Dependencies/Assumptions:
 *
 * Usage:
 *
 * History:
 * ddmmmyyyy userid description (Change Code)
 * 15Aug2022 craig File created.
 \******************************************************************************/
import React, {Fragment, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Button, Col, Container, Form, Row} from "react-bootstrap";
import {IUser} from "../../../../models/user/IUser";
import {getSessionCookie} from "../../../../context/context";
import {PasswordRestrictions} from "../../../../components/PasswordRestrictions";
import {Formik, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import {getDataPost} from "../../../../models/data/Data";
import {IOutcome} from "../../../../models/request/Outcome";

export const ResetPassword: React.FC = (props) => {

    const navigate = useNavigate();

    const schema = Yup.object().shape({
        password: Yup.string().required().min(8, 'Must be 8 or more characters').max(15, 'Must be 15 or less characters'),
        confirmPassword: Yup.string().required().oneOf([Yup.ref('password')], 'Passwords must match'),
    });

    const {t, d} = useParams();
    const [sessionUser] = useState<IUser>(getSessionCookie());
    const [resetMsg, setResetMsg] = useState<string>("");

    const buildForm = () => {
        return (
            <Formik
                validationSchema={schema}
                //onSubmit={ (values) => console.log(JSON.stringify(values, null, 2))}
                onSubmit={(values) => {
                    const data = `${encodeURIComponent("password")}=${encodeURIComponent(values.password)}`;
                    const resetURL = process.env.REACT_APP_API_URL + "/api/form/reset/" + t + "/" + d;
                    getDataPost(resetURL, data).then(response => {
                        const outcome: IOutcome = response;
                        if (outcome.Success) {
                            navigate("/login");
                        } else {
                            setResetMsg(outcome.RequestMsg);
                        }
                    })
                }}
                initialValues={{
                    password: '',
                    confirmPassword: '',
                }}
                validateOnChange={false}
                validateOnBlur={false}>
                {({
                      handleSubmit,
                      handleChange,
                      handleBlur,
                      values,
                      touched,
                      isValid,
                      errors,
                      isSubmitting,
                  }) => (
                    <Form className="form-layout" onSubmit={handleSubmit} noValidate={true}>
                        <Row className="mb-3">
                            <Form.Group as={Col} className="col-left" controlId="formPassword">
                                <Form.Label>Password *</Form.Label>
                                <Form.Control required className="text-mid-width"
                                              name={"password"}
                                              type="password"
                                              placeholder="Enter new password"
                                              isValid={touched.password && !errors.password}
                                              onChange={handleChange}/>
                                <Form.Text className="text-muted">Enter your new password.</Form.Text>
                                <Form.Control.Feedback type="invalid">
                                    Please enter a new password
                                </Form.Control.Feedback>
                                <ErrorMessage name={"password"}
                                              render={msg => <div className={"form-error-msg"}>{msg}</div>}/>
                            </Form.Group>
                        </Row>
                        <Row className="mb-3">
                            <Form.Group as={Col} className="col-left" controlId="formConfirmPassword">
                                <Form.Label>Confirm Password *</Form.Label>
                                <Form.Control
                                    required
                                    name={"confirmPassword"}
                                    type="password"
                                    className="text-mid-width"
                                    placeholder="Reenter password"
                                    isValid={touched.confirmPassword && !errors.confirmPassword}
                                    onChange={handleChange}/>
                                <Form.Text className="text-muted">Confirm your new password</Form.Text>
                                <Form.Control.Feedback type="invalid">
                                    Please confirm password
                                </Form.Control.Feedback>
                                <ErrorMessage name={"confirmPassword"}
                                              render={msg => <div className={"form-error-msg"}>{msg}</div>}/>
                            </Form.Group>
                        </Row>
                        <Button variant="primary" className="btn-submit" type="submit" disabled={isSubmitting}>
                            Submit
                        </Button>
                    </Form>
                )}
            </Formik>
        )

    }

    const buildInstructions = () => {
        if (sessionUser.UserID <= 0) {
            return (
                PasswordRestrictions()
            )
        }
        return <Fragment/>
    }

    return (
        <Container className="form-container" fluid>
            <Row>
                <Col className="page-title">
                    <h1>Reset Password</h1>
                    <div className={"error-message"}>{resetMsg}</div>
                </Col>
            </Row>
            {buildForm()}
            {buildInstructions()}
        </Container>
    )
}